import { Button, Input, Select } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { MovementTable } from '@/components/journey/MovementTable'
import { PageContainer } from '@/components/common/PageContainer'
import { PageHeader } from '@/components/common/PageHeader'
import { SectionCard } from '@/components/common/SectionCard'
import { FilterField } from '@/components/common/LookupBar'
import { PageEmpty, PageError } from '@/components/common/PageStates'
import { dashboardService } from '@/services'
import { useRegionStore } from '@/stores/regionStore'

type TagKind = 'all' | 'scannable' | 'non_scan'

const TAG_KIND_OPTIONS: { value: TagKind; label: string }[] = [
  { value: 'all', label: 'All tags & line items' },
  { value: 'scannable', label: 'Scannable RFID tags' },
  { value: 'non_scan', label: 'Non-scannable line items' },
]

export default function MovementsPage() {
  const regionView = useRegionStore((s) => s.view)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(25)
  const [rfidCode, setRfidCode] = useState('')
  const [document, setDocument] = useState('')
  const [transferType, setTransferType] = useState('')
  const [tagKind, setTagKind] = useState<TagKind>('all')
  const [applied, setApplied] = useState({
    rfidCode: '',
    document: '',
    transferType: '',
    tagKind: 'all' as TagKind,
  })

  const q = useQuery({
    queryKey: ['movements', regionView, page, limit, applied],
    queryFn: () =>
      dashboardService.getMovements({
        page,
        limit,
        rfid_code: applied.rfidCode || undefined,
        document: applied.document || undefined,
        transfer_type: applied.transferType || undefined,
        tag_kind: applied.tagKind,
      }),
  })

  const apply = () => {
    setPage(1)
    setApplied({ rfidCode, document, transferType, tagKind })
  }

  return (
    <PageContainer fluid>
      <PageHeader
        title="Movements"
        subtitle="Paginated movement history with filters for tags, documents, and line-item type."
      />

      <SectionCard title="Filters" subtitle="Refine the movement list, then apply.">
        <div className="filter-bar">
          <FilterField label="RFID code" className="w-48">
            <Input
              placeholder="RFID code"
              value={rfidCode}
              onChange={(e) => setRfidCode(e.target.value)}
              allowClear
              onPressEnter={apply}
            />
          </FilterField>
          <FilterField label="Document" className="w-48">
            <Input
              placeholder="Document"
              value={document}
              onChange={(e) => setDocument(e.target.value)}
              allowClear
              onPressEnter={apply}
            />
          </FilterField>
          <FilterField label="Transfer type" className="w-40">
            <Input
              placeholder="Transfer type"
              value={transferType}
              onChange={(e) => setTransferType(e.target.value)}
              allowClear
              onPressEnter={apply}
            />
          </FilterField>
          <FilterField label="Tag kind" className="w-56">
            <Select
              className="w-full"
              value={tagKind}
              options={TAG_KIND_OPTIONS}
              onChange={(v: TagKind) => setTagKind(v)}
            />
          </FilterField>
          <Button type="primary" onClick={apply}>
            Apply
          </Button>
        </div>
      </SectionCard>

      {q.isError ? (
        <PageError message={(q.error as { message?: string })?.message} />
      ) : (
        <SectionCard
          title="Results"
          count={q.data?.pagination.total}
          subtitle={q.isFetching ? 'Updating…' : undefined}
          variant="table"
        >
          {!q.isLoading && (q.data?.data.length ?? 0) === 0 ? (
            <PageEmpty description="No movements match the current filters." />
          ) : (
            <MovementTable
              rows={q.data?.data ?? []}
              loading={q.isLoading || q.isFetching}
              pagination={{
                page: q.data?.pagination.page ?? page,
                pageSize: q.data?.pagination.limit ?? limit,
                total: q.data?.pagination.total ?? 0,
              }}
              onPageChange={(p, ps) => {
                setPage(p)
                setLimit(ps)
              }}
            />
          )}
        </SectionCard>
      )}
    </PageContainer>
  )
}
